/**
 * ClusterBomb.ts
 * Pooled cluster-bomb payload dropped by `BomberDrone`.
 *
 * Travels diagonally (inherits the bomber's leftward drift at
 * release + a configured downward velocity), then detonates after
 * {@link BOMBER_DRONE_BOMB_FUSE_S} and spawns
 * {@link BOMBER_DRONE_SHRAPNEL_COUNT} radial shrapnel pieces. The
 * bomb dies on detonation, on ship contact (consumed by `shipDamage`),
 * or on the left-edge cull line — whichever fires first.
 *
 * The bomb DOES NOT spawn its own shrapnel — it just sets
 * `detonated = true` on fuse expiry. The owning engine module
 * (`bomberDroneDrop`) is responsible for reading that flag and
 * acquiring shrapnel from the sibling pool. Keeps the entity free of
 * cross-pool acquisition concerns and matches the
 * `BossMissile` → `bossMissileBarrage` ownership pattern.
 *
 * Pool contract: implements `reset()` for `ObjectPool` recycling.
 * Always acquire from `clusterBombPool` (see `bomberDroneDrop.ts`)
 * rather than `new`-ing directly outside of pool pre-warm.
 *
 * NO React imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module ClusterBomb
 */

import { BaseEntity } from './BaseEntity';
import type { LevelRuntime } from '../engine/gameState';
import { spaceState } from '../engine/spaceState';
import {
  BOMBER_DRONE_BOMB_WIDTH,
  BOMBER_DRONE_BOMB_HEIGHT,
  BOMBER_DRONE_BOMB_DROP_VY_PX_S,
  BOMBER_DRONE_BOMB_FUSE_S,
  BOMBER_DRONE_BOMB_OFFSCREEN_MARGIN_PX,
  BOMBER_DRONE_DRIFT_SPEED,
} from '../../constants/spaceEnemyConstants';

/**
 * ClusterBomb — pooled left+down-travelling payload with a fuse timer.
 * Renderer reads its hitbox; `bomberDroneDrop` writes its lifecycle;
 * `shipDamage` consumes it on overlap.
 */
export class ClusterBomb extends BaseEntity {
  /** Monotonic ID counter for stable renderer keys across pool recycling. */
  private static nextId = 1;

  /** Unique spawn identifier — re-issued each `spawn()` call. */
  public spawnId = 0;

  /** True while the bomb is mid-fuse. Cleared on detonation or contact. */
  public active = false;

  /**
   * True the tick the bomb's fuse expires. Latched (never cleared by
   * the bomb itself) so the owning system can read it during its
   * tick-end reap pass without racing the next `update()`. Reset to
   * false in `reset()` when the pool recycles the slot.
   */
  public detonated = false;

  /** Seconds remaining until detonation. Counts down by `dt` each tick. */
  private fuseRemaining = 0;

  constructor() {
    super(0, 0, BOMBER_DRONE_BOMB_WIDTH, BOMBER_DRONE_BOMB_HEIGHT);
    this.dead = true;
  }

  /**
   * Initialise the bomb at a world position. Inherits the bomber's
   * leftward drift via `-BOMBER_DRONE_DRIFT_SPEED` and adds the
   * configured downward velocity for the "thrown forward and down"
   * arc that reads as a dropped payload.
   *
   * @param x - Spawn X in world space (logical px, top-left of hitbox).
   * @param y - Spawn Y in world space (logical px, top-left of hitbox).
   */
  public spawn(x: number, y: number): void {
    this.spawnId = ClusterBomb.nextId++;
    this.x = x;
    this.y = y;
    this.vx = -BOMBER_DRONE_DRIFT_SPEED;
    this.vy = BOMBER_DRONE_BOMB_DROP_VY_PX_S;
    this.fuseRemaining = BOMBER_DRONE_BOMB_FUSE_S;
    this.detonated = false;
    this.dead = false;
    this.active = true;
  }

  /** Reset state for pool recycling. Called by `ObjectPool.release()`. */
  public reset(): void {
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.fuseRemaining = 0;
    this.detonated = false;
    this.dead = true;
    this.active = false;
  }

  /**
   * Read the bomb's current world-space X used for shrapnel spawn
   * positioning. Returns the bomb's centre so the shrapnel fan reads
   * as radiating from the centre of the detonation rather than the
   * top-left corner of the hitbox.
   */
  public detonationCentreX(): number {
    return this.x + this.width / 2;
  }

  /**
   * Read the bomb's current world-space Y used for shrapnel spawn
   * positioning. Returns the bomb's centre for the same reason as
   * {@link detonationCentreX}.
   */
  public detonationCentreY(): number {
    return this.y + this.height / 2;
  }

  /**
   * Advance the bomb by one fixed timestep. Order of resolution per
   * tick: integrate position → check left-cull → tick fuse → flip
   * `detonated` on expiry. Detonation marks the bomb dead but leaves
   * `detonated = true` for the owning system to read before recycling.
   *
   * @param dt    - Fixed timestep in seconds.
   * @param level - Unused; kept for `BaseEntity.update` contract.
   */
  public update(dt: number, level: LevelRuntime): void {
    void level;
    if (!this.active) return;

    this.x += this.vx * dt;
    this.y += this.vy * dt;

    const leftCullX = spaceState.cameraX - BOMBER_DRONE_BOMB_OFFSCREEN_MARGIN_PX;
    if (this.x + this.width < leftCullX) {
      this.dead = true;
      this.active = false;
      return;
    }

    this.fuseRemaining -= dt;
    if (this.fuseRemaining <= 0) {
      this.detonated = true;
      this.dead = true;
      this.active = false;
    }
  }
}
